import React, { memo, useEffect, useRef } from 'react'

import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types'

import AcquiredModelCard from '../AcquiredModelCard/AcquiredModelCard'
import LoadingCard from '../LoadingCard/LoadingCard'
import UploadTile from '../UploadTile/UploadTile'

function Grid({
  items,
  selectedId = null,
  hasNextPage,
  isFetchingNextPage,
  skeletonCount,
  onSelect,
  onUploadStart,
  onLoadMore,
}) {
  const scrollContainerRef = useRef(null)
  const sentinelRef = useRef(null)

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    const sentinel = sentinelRef.current
    if (!scrollContainer || !sentinel || !hasNextPage || isFetchingNextPage) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onLoadMore()
        }
      },
      { root: scrollContainer, rootMargin: '100px', threshold: 0 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, onLoadMore])

  return (
    <div ref={scrollContainerRef} className="three-d-model-picker__grid">
      <UploadTile onUploadStart={onUploadStart} />
      {items.map(({ id, ...rest }) => (
        <AcquiredModelCard
          key={`acquired-model-${id}`}
          id={id}
          isSelected={String(selectedId) === String(id)}
          onSelect={onSelect}
          {...rest}
        />
      ))}
      {isFetchingNextPage && Array.from({ length: skeletonCount }, (_, index) => (
        <LoadingCard key={`loading-card-${index}`} />
      ))}
      {hasNextPage && (
        <div ref={sentinelRef} className="three-d-model-picker__sentinel" aria-hidden="true" />
      )}
    </div>
  )
}

Grid.propTypes = {
  items: arrayOf(shape({
    id: oneOfType([number, string]).isRequired,
    title: string.isRequired,
    thumbnailUrl: string.isRequired,
    viewerCompatible: bool.isRequired,
  })).isRequired,
  selectedId: oneOfType([number, string]),
  hasNextPage: bool.isRequired,
  isFetchingNextPage: bool.isRequired,
  skeletonCount: number.isRequired,
  onSelect: func.isRequired,
  onUploadStart: func.isRequired,
  onLoadMore: func.isRequired,
}

export default memo(Grid)
